import React, { useCallback, useEffect, useRef, useState } from 'react';

export const QUOTE_PDF_PAGE_WIDTH = 794;

/**
 * Scales fixed-width quote PDF pages to fit the available width (mobile-friendly).
 * Uses transform: scale() instead of CSS zoom (unsupported / buggy on iOS Safari).
 */
export function QuotePreviewScaler({
  children,
  className = '',
  zoom = 1,
  pageWidth = QUOTE_PDF_PAGE_WIDTH,
  minScale = 0.32,
  maxScale = 1.5,
}) {
  const hostRef = useRef(null);
  const innerRef = useRef(null);
  const [fitScale, setFitScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  const measure = useCallback(() => {
    const host = hostRef.current;
    const inner = innerRef.current;
    if (!host || !inner) return;

    const available = host.clientWidth;
    const fit = available > 0 ? Math.min(1, available / pageWidth) : 1;
    setFitScale(fit);
    setContentHeight(inner.scrollHeight);
  }, [pageWidth]);

  useEffect(() => {
    const host = hostRef.current;
    const inner = innerRef.current;
    if (!host || !inner) return undefined;

    measure();

    const t1 = window.setTimeout(measure, 100);
    const t2 = window.setTimeout(measure, 600);

    const ro = new ResizeObserver(() => measure());
    ro.observe(host);

    const mo = new MutationObserver(() => measure());
    mo.observe(inner, { childList: true, subtree: true, attributes: true });

    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [measure, children]);

  const scale = Math.max(minScale, Math.min(maxScale, fitScale * zoom));
  const scaledWidth = pageWidth * scale;
  const scaledHeight = contentHeight > 0 ? contentHeight * scale : undefined;

  return (
    <div ref={hostRef} className={`quote-preview-scaler-host w-full min-w-0 ${className}`}>
      <div
        className="quote-preview-scaler-clip mx-auto"
        style={{
          width: scaledWidth,
          height: scaledHeight,
        }}
      >
        <div
          ref={innerRef}
          className="quote-preview-scaler-inner"
          style={{
            width: pageWidth,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default QuotePreviewScaler;
